// /src/Components/Services/DomainServices/Templates.jsx
import React, { useMemo, useState } from "react";
import {
  Copy,
  ExternalLink,
  ImageIcon,
  Layers3,
  Loader2,
  RefreshCcw,
  Search,
  Sparkles,
} from "lucide-react";
import { toast } from "react-hot-toast";

export default function Templates({
  templates = [],
  templatesLoading = false,
  fetchTemplates,
}) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredTemplates = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    if (!q) return templates;

    return templates.filter((template) => {
      return (
        String(template?.template_name || "").toLowerCase().includes(q) ||
        String(template?.template_code || "").toLowerCase().includes(q) ||
        String(template?.id || "").toLowerCase().includes(q)
      );
    });
  }, [templates, searchTerm]);

  const handleCopy = async (text, label = "Copied") => {
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="rounded-2xl border bg-white p-3">
      <div className="rounded-2xl border p-4">
        {/* Header */}
        <div className="mb-4 flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
              <Sparkles size={14} />
              Template Library
            </div>

            <h2 className="text-lg font-semibold text-gray-900">
              Website Templates
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              Search, preview and verify all available Chaafo website themes.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchTemplates}
            disabled={templatesLoading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCcw
              size={16}
              className={templatesLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {/* Stats + Search */}
        <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-12">
         

          <div className="lg:col-span-8">
            <div className="rounded-2xl border bg-white p-4 shadow-sm">
              <label className="mb-1 block text-xs font-semibold text-gray-500">
                Search Templates
              </label>

              <div className="relative">
                <Search
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />

                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by template name, code or ID..."
                  className="h-11 w-full rounded-xl border bg-white pl-10 pr-3 text-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              <p className="mt-2 text-xs text-gray-400">
                Showing{" "}
                <span className="font-semibold text-gray-700">
                  {filteredTemplates.length}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-700">
                  {templates.length}
                </span>{" "}
                templates.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {templatesLoading ? (
          <TemplateSkeletonGrid />
        ) : filteredTemplates.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template?.id || template?.template_code}
                template={template}
                onCopy={handleCopy}
              />
            ))}
          </div>
        ) : (
          <EmptyTemplates searchTerm={searchTerm} />
        )}
      </div>
    </div>
  );
}

function TemplateCard({ template, onCopy }) {
  const [imageFailed, setImageFailed] = useState(false);

  const templateName = template?.template_name || "Untitled Template";
  const templateCode = template?.template_code || "—";
  const templateId = template?.id || "—";
  const templateUrl = template?.template_url || "";

  return (
    <div className="group overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-md">
      {/* Preview */}
      <div className="relative h-48 overflow-hidden bg-gray-100">
        {templateUrl && !imageFailed ? (
          <img
            src={templateUrl}
            alt={templateName}
            onError={() => setImageFailed(true)}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-gray-400">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-white">
              <ImageIcon size={24} />
            </div>
            <p className="mt-2 text-sm font-medium">No preview available</p>
          </div>
        )}

        <div className="absolute left-3 top-3 rounded-full border border-white/30 bg-white/90 px-2.5 py-1 text-xs font-bold text-emerald-700 shadow-sm backdrop-blur">
          ID: {templateId}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="mb-3">
          <h3 className="truncate text-base font-semibold text-gray-900">
            {templateName}
          </h3>

          <p className="mt-1 truncate font-mono text-xs text-gray-400">
            {templateCode}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-2">
          {/* <InfoRow label="Template ID" value={templateId} /> */}
          <InfoRow label="Template Code" value={templateCode} />
        </div>

        <div className="mt-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => onCopy(templateCode, "Template code copied")}
            className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-xl border bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            <Copy size={13} />
            Copy Code
          </button>

          {templateUrl ? (
            <a
              href={templateUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-3 text-xs font-semibold text-white hover:bg-[#00ab6d]"
            >
              <ExternalLink size={13} />
              Preview
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="inline-flex h-9 flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-gray-200 px-3 text-xs font-semibold text-gray-500"
            >
              <ExternalLink size={13} />
              Preview
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="rounded-xl border bg-gray-50 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <p className="mt-0.5 break-all font-mono text-xs font-semibold text-gray-700">
        {value || "—"}
      </p>
    </div>
  );
}

function TemplateSkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <div
          key={index}
          className="overflow-hidden rounded-2xl border bg-white shadow-sm"
        >
          <div className="h-48 animate-pulse bg-gray-200" />

          <div className="p-4">
            <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
            <div className="mt-2 h-3 w-44 animate-pulse rounded bg-gray-200" />

            <div className="mt-4 space-y-2">
              <div className="h-10 animate-pulse rounded-xl bg-gray-100" />
              <div className="h-10 animate-pulse rounded-xl bg-gray-100" />
            </div>

            <div className="mt-4 flex gap-2">
              <div className="h-9 flex-1 animate-pulse rounded-xl bg-gray-200" />
              <div className="h-9 flex-1 animate-pulse rounded-xl bg-gray-200" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyTemplates({ searchTerm }) {
  return (
    <div className="rounded-2xl border bg-white px-4 py-14 text-center">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gray-50 text-gray-400">
        <Layers3 size={24} />
      </div>

      <h3 className="mt-4 text-base font-semibold text-gray-900">
        No templates found
      </h3>

      <p className="mt-1 text-sm text-gray-500">
        {searchTerm
          ? "No template matches your search."
          : "Templates will appear here after refresh."}
      </p>
    </div>
  );
}